import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { ICRUDOptions } from '@phx-erp/shared';
import { BaseListService } from '../../../@core/services/base-list.service';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { SystemService } from '../../../@core/services/system.service';
import { DELETE_ADDRESS_BANK_ACCOUNT_LINK, GET_ADDRESS_BANK_ACCOUNT_LINK, GET_ADDRESS_BANK_ACCOUNT_LINKS, SAVE_ADDRESS_BANK_ACCOUNT_LINK } from '../../../data/definitions/address-bank-account-link.definition';
import { QueryResult } from '../../../data/query-result';
import { BaseDataService } from '../../../data/services/base-data.service';
import { AddressBankAccountLink, AddressBankAccountLinkInput, DeleteAddressBankAccountLinkMutation, DeleteAddressBankAccountLinkMutationVariables, DeletionResponse, GetAddressBankAccountLinkQuery, GetAddressBankAccountLinkQueryVariables, GetAddressBankAccountLinksQuery, GetAddressBankAccountLinksQueryVariables, SaveAddressBankAccountLinkMutation, SaveAddressBankAccountLinkMutationVariables, SearchInput } from '../../../generated/generated-types';
import { AppInfoService } from '../../../shared/services/app-info.service';

@Injectable({
  providedIn: 'root'
})
export class AddressBankAccountLinkService extends BaseListService<AddressBankAccountLink> {

  constructor(
    private baseDataService: BaseDataService,
    private appInfo: AppInfoService,
    private i18n: I18nService,
    private systemService: SystemService,
  ) {
    super();
  }

  getItems(searchInput?: SearchInput, o?: ICRUDOptions): QueryResult<GetAddressBankAccountLinksQuery, GetAddressBankAccountLinksQueryVariables> {

    const definition = GET_ADDRESS_BANK_ACCOUNT_LINKS;
    if (o?.currentColumnConfig) {
      this.onlyKeepSelectedGraphQLNodesNew(definition, o.currentColumnConfig.baseView, o.currentColumnConfig.columns);
    }
    return this.baseDataService.query<GetAddressBankAccountLinksQuery, GetAddressBankAccountLinksQueryVariables>(
      definition, {
      searchInput
    });
  }

  getItem(id: string): QueryResult<GetAddressBankAccountLinkQuery, GetAddressBankAccountLinkQueryVariables> {
    return this.baseDataService.query<GetAddressBankAccountLinkQuery, GetAddressBankAccountLinkQueryVariables>(GET_ADDRESS_BANK_ACCOUNT_LINK, {
      id
    });
  }

  getItemStream(id: string): Observable<AddressBankAccountLink> {
    return this.getItem(id).mapStream(x => x.getAddressBankAccountLink) as Observable<AddressBankAccountLink>;
  }

  async getItemData(id: string): Promise<AddressBankAccountLink> {
    const result = await lastValueFrom(this.getItem(id).single$);
    return result.getAddressBankAccountLink as AddressBankAccountLink;
  }

  async deleteItem(id: string): Promise<DeletionResponse> {
    const result = await lastValueFrom(this.baseDataService.mutate<DeleteAddressBankAccountLinkMutation, DeleteAddressBankAccountLinkMutationVariables>(
      DELETE_ADDRESS_BANK_ACCOUNT_LINK, { id }));
    return result.deleteAddressBankAccountLink;
  }

  async saveItem(input: AddressBankAccountLinkInput): Promise<AddressBankAccountLink> {
    // Validate the object before saving - it' necessary to validate the object before saving it
    let validated = await this.systemService.validateObject<AddressBankAccountLinkInput>(input, 'AddressBankAccountLinkInput');
    // Save the object
    const saved = await lastValueFrom(this.baseDataService.mutate<SaveAddressBankAccountLinkMutation, SaveAddressBankAccountLinkMutationVariables>(
      SAVE_ADDRESS_BANK_ACCOUNT_LINK, {
      input: validated
    }
    ));
    return saved.saveAddressBankAccountLink as AddressBankAccountLink;
  }
}
